/**
 * AiCloud API Keys - Kullanicinin LLM key'lerini yonetir
 * cloud_api / local_llm / webllm / petals / cloudflare / azure / aws / gcp
 */

import axios from 'axios'
import {
    AlertCircle, Check, ChevronDown, ChevronUp,
    Eye, EyeOff, Globe, Key, Loader2, Plus,
    RefreshCw, Server, Trash2, Wifi, WifiOff, Zap
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../../config/api'

// ─── Tipler ───────────────────────────────────────────────────────────────────

interface ApiKey {
  id: number
  provider: string
  provider_type: string
  key_name: string
  key_masked: string
  model_name: string
  is_active: boolean
  pool_mode: boolean
  local_endpoint: string
  endpoint: string
  region: string
  account_id: string
  usage_count: number
  total_tokens_used: number
  total_flz_earned: number
  created_at: string
  last_used_at: string | null
  // runtime
  testing?: boolean
  test_result?: { online: boolean; message: string; latency_ms: number }
}

interface Stats {
  total_keys: number
  active_keys: number
  pool_keys: number
  total_tokens_used: number
  total_flz_earned: number
  monthly_flz_earned: number
}

type Tab = 'cloud' | 'local' | 'webllm' | 'petals'

// ─── Sabitler ─────────────────────────────────────────────────────────────────

const CLOUD_PROVIDERS = [
  { id: 'gemini',      name: 'Google Gemini',      models: ['gemini-2.5-flash', 'gemini-2.0-flash', 'gemini-1.5-pro'] },
  { id: 'mistral',     name: 'Mistral AI',          models: ['mistral-small-latest', 'mistral-large-latest'] },
  { id: 'groq',        name: 'Groq LPU',            models: ['llama-3.3-70b-versatile', 'mixtral-8x7b-32768'] },
  { id: 'azure',       name: 'Azure OpenAI',        models: ['gpt-4o', 'gpt-4-turbo', 'gpt-4o-mini'] },
  { id: 'dashscope',   name: 'Alibaba DashScope',   models: ['qwen-max', 'qwen-plus', 'qwen-turbo'] },
  { id: 'aws',         name: 'AWS Bedrock',         models: ['anthropic.claude-3-5-sonnet-20241022-v2:0', 'amazon.titan-text-express-v1'] },
  { id: 'cloudflare',  name: 'Cloudflare Workers AI', models: ['@cf/meta/llama-3.1-8b-instruct', '@cf/mistral/mistral-7b-instruct-v0.1'] },
  { id: 'huggingface', name: 'HuggingFace',         models: ['mistralai/Mistral-7B-Instruct-v0.3'] },
]

function authHeader() {
  const token = localStorage.getItem('token')
  return { Authorization: `Bearer ${token}` }
}

// ─── Ana Bileşen ──────────────────────────────────────────────────────────────

export default function ApiKeysPage() {
  const [keys, setKeys]       = useState<ApiKey[]>([])
  const [stats, setStats]     = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab]         = useState<Tab>('cloud')
  const [showAdd, setShowAdd] = useState(false)
  const [error, setError]     = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Form state
  const [form, setForm] = useState({
    provider: 'gemini', provider_type: 'cloud_api',
    key_name: '', api_key: '', model_name: 'gemini-2.5-flash',
    local_endpoint: '', endpoint: '', region: '', account_id: '',
    pool_mode: false,
  })
  const [saving, setSaving]   = useState(false)
  const [showKey, setShowKey] = useState<Record<number, boolean>>({})

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [keysRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/aicloud/api-keys`, { headers: authHeader() }),
        axios.get(`${API_URL}/api/aicloud/api-keys/stats`, { headers: authHeader() }),
      ])
      setKeys((keysRes.data as { keys: ApiKey[] }).keys ?? [])
      setStats(statsRes.data as Stats)
    } catch { setKeys([]) }
    finally { setLoading(false) }
  }

  useEffect(() => { void fetchAll() }, [])

  // Sekmeye gore filtrele
  const filtered = keys.filter(k => {
    if (tab === 'cloud')  {return k.provider_type === 'cloud_api'}
    if (tab === 'local')  {return k.provider_type === 'local_llm'}
    if (tab === 'webllm') {return k.provider_type === 'webllm'}
    if (tab === 'petals') {return k.provider_type === 'petals'}
    return true
  })

  const handleAdd = async () => {
    if (!form.key_name) { setError('Label required'); return }
    if (form.provider_type === 'cloud_api' && !form.api_key) { setError('API key required'); return }
    setSaving(true); setError(null)
    try {
      await axios.post(`${API_URL}/api/aicloud/api-keys`, form, { headers: authHeader() })
      setSuccess('Key added')
      setShowAdd(false)
      setForm({ provider: 'gemini', provider_type: 'cloud_api', key_name: '', api_key: '',
                model_name: 'gemini-2.5-flash', local_endpoint: '', endpoint: '',
                region: '', account_id: '', pool_mode: false })
      void fetchAll()
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err.response?.data?.detail ?? 'Failed')
    } finally { setSaving(false) }
  }

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/api/aicloud/api-keys/${id}`, { headers: authHeader() })
      setKeys(prev => prev.filter(k => k.id !== id))
    } catch { setError('Delete failed') }
  }

  const handleTest = async (id: number) => {
    setKeys(prev => prev.map(k => k.id === id ? { ...k, testing: true } : k))
    try {
      const res = await axios.get(`${API_URL}/api/aicloud/api-keys/test/${id}`, { headers: authHeader() })
      const result = res.data as { online: boolean; message: string; latency_ms: number }
      setKeys(prev => prev.map(k => k.id === id
        ? { ...k, testing: false, test_result: result, is_active: result.online }
        : k))
    } catch {
      setKeys(prev => prev.map(k => k.id === id
        ? { ...k, testing: false, test_result: { online: false, message: 'Request failed', latency_ms: 0 } }
        : k))
    }
  }

  const handleTogglePool = async (id: number, current: boolean) => {
    try {
      await axios.patch(`${API_URL}/api/aicloud/api-keys/${id}`,
        { pool_mode: !current }, { headers: authHeader() })
      setKeys(prev => prev.map(k => k.id === id ? { ...k, pool_mode: !current } : k))
    } catch { setError('Update failed') }
  }

  const selectedProvider = CLOUD_PROVIDERS.find(p => p.id === form.provider)

  return (
    <div>
      {/* Üst başlık + istatistikler */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>API Keys & LLM Connections</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            Add cloud keys, local LLM endpoints, WebLLM or Petals nodes. Keys are Fernet-encrypted.
          </p>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '8px 14px', background: '#3b82f6', color: 'white',
          border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600
        }}>
          <Plus size={14} /> Add
        </button>
      </div>

      {/* Stats bar */}
      {stats && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[
            { label: 'Total Keys', value: stats.total_keys },
            { label: 'Active', value: stats.active_keys, color: '#10b981' },
            { label: 'In Pool', value: stats.pool_keys, color: '#f59e0b' },
            { label: 'Tokens Used', value: stats.total_tokens_used.toLocaleString() },
            { label: 'FLZ Earned', value: stats.total_flz_earned.toFixed(2), color: '#8b5cf6' },
            { label: 'This Month', value: `${stats.monthly_flz_earned.toFixed(2)} FLZ`, color: '#10b981' },
          ].map(s => (
            <div key={s.label} style={{ padding: '10px 16px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', minWidth: '100px' }}>
              <div style={{ fontSize: '18px', fontWeight: 700, color: s.color ?? 'var(--text-primary)' }}>{s.value}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Bildirimler */}
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '14px' }}>
          <AlertCircle size={14} /> {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>✕</button>
        </div>
      )}
      {success && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', color: '#10b981', fontSize: '13px', marginBottom: '14px' }}>
          <Check size={14} /> {success}
          <button onClick={() => setSuccess(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#10b981' }}>✕</button>
        </div>
      )}

      {/* Add form */}
      {showAdd && (
        <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Add Connection</h3>

          {/* Tip seçimi */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            {[
              { type: 'cloud_api', label: 'Cloud API', icon: <Globe size={13} /> },
              { type: 'local_llm', label: 'Local LLM', icon: <Server size={13} /> },
              { type: 'webllm',    label: 'WebLLM',    icon: <Zap size={13} /> },
              { type: 'petals',    label: 'Petals P2P', icon: <Wifi size={13} /> },
            ].map(t => (
              <button key={t.type} onClick={() => setForm(f => ({ ...f, provider_type: t.type }))}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
                  background: form.provider_type === t.type ? '#3b82f6' : 'var(--body-bg)',
                  color: form.provider_type === t.type ? 'white' : 'var(--text-secondary)',
                  border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            {/* Provider (cloud_api icin) */}
            {form.provider_type === 'cloud_api' && (
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px' }}>Provider</label>
                <select value={form.provider}
                  onChange={e => {
                    const p = CLOUD_PROVIDERS.find(x => x.id === e.target.value)
                    setForm(f => ({ ...f, provider: e.target.value, model_name: p?.models[0] ?? '' }))
                  }}
                  style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }}>
                  {CLOUD_PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
            {/* Model (cloud_api icin) */}
            {form.provider_type === 'cloud_api' && (
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px' }}>Default Model</label>
                <select value={form.model_name}
                  onChange={e => setForm(f => ({ ...f, model_name: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }}>
                  {selectedProvider?.models.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Label */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px' }}>Label</label>
            <input type="text" placeholder="e.g. My Gemini Key" value={form.key_name}
              onChange={e => setForm(f => ({ ...f, key_name: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
          </div>

          {/* API Key (cloud_api icin) */}
          {form.provider_type === 'cloud_api' && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px' }}>API Key</label>
              <input type="password" placeholder="sk-... or AIza..." value={form.api_key}
                onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
            </div>
          )}

          {/* Azure ek alanlar */}
          {form.provider_type === 'cloud_api' && form.provider === 'azure' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px' }}>Endpoint URL</label>
                <input type="text" placeholder="https://xxx.openai.azure.com" value={form.endpoint}
                  onChange={e => setForm(f => ({ ...f, endpoint: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px' }}>Region</label>
                <input type="text" placeholder="eastus" value={form.region}
                  onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
              </div>
            </div>
          )}

          {/* AWS ek alanlar */}
          {form.provider_type === 'cloud_api' && form.provider === 'aws' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px' }}>Region</label>
                <input type="text" placeholder="us-east-1" value={form.region}
                  onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px' }}>Account ID (optional)</label>
                <input type="text" placeholder="123456789012" value={form.account_id}
                  onChange={e => setForm(f => ({ ...f, account_id: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
              </div>
            </div>
          )}

          {/* Cloudflare ek alanlar */}
          {form.provider_type === 'cloud_api' && form.provider === 'cloudflare' && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px' }}>Account ID</label>
              <input type="text" placeholder="abc123..." value={form.account_id}
                onChange={e => setForm(f => ({ ...f, account_id: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
            </div>
          )}

          {/* Local LLM endpoint */}
          {form.provider_type === 'local_llm' && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px' }}>Endpoint (LM Studio / Ollama)</label>
              <input type="text" placeholder="http://localhost:1234" value={form.local_endpoint}
                onChange={e => setForm(f => ({ ...f, local_endpoint: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
            </div>
          )}

          {/* Petals endpoint */}
          {form.provider_type === 'petals' && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px' }}>Petals Node Endpoint</label>
              <input type="text" placeholder="http://localhost:8080" value={form.local_endpoint}
                onChange={e => setForm(f => ({ ...f, local_endpoint: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
            </div>
          )}

          {/* WebLLM bilgi */}
          {form.provider_type === 'webllm' && (
            <div style={{ padding: '12px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
              <Zap size={14} style={{ display: 'inline', marginRight: '6px', color: '#3b82f6' }} />
              WebLLM runs entirely in your browser using WebGPU. No API key needed. Enable it here to register your browser GPU as a contribution.
            </div>
          )}

          {/* Pool mode toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', padding: '10px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '8px' }}>
            <input type="checkbox" id="pool_mode" checked={form.pool_mode}
              onChange={e => setForm(f => ({ ...f, pool_mode: e.target.checked }))}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
            <label htmlFor="pool_mode" style={{ fontSize: '13px', cursor: 'pointer' }}>
              <span style={{ fontWeight: 600, color: '#f59e0b' }}>Join Pool</span>
              <span style={{ color: 'var(--text-secondary)', marginLeft: '6px' }}>— Share with platform, earn FLZ tokens</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => { void handleAdd() }} disabled={saving}
              style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setShowAdd(false)}
              style={{ padding: '8px 16px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Sekmeler */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '1px solid var(--border-color)', paddingBottom: '0' }}>
        {([
          { id: 'cloud',  label: 'Cloud API',   icon: <Globe size={13} /> },
          { id: 'local',  label: 'Local LLM',   icon: <Server size={13} /> },
          { id: 'webllm', label: 'WebLLM',       icon: <Zap size={13} /> },
          { id: 'petals', label: 'Petals P2P',   icon: <Wifi size={13} /> },
        ] as { id: Tab; label: string; icon: React.ReactNode }[]).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 14px',
              background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13px',
              color: tab === t.id ? '#3b82f6' : 'var(--text-secondary)',
              borderBottom: tab === t.id ? '2px solid #3b82f6' : '2px solid transparent',
              marginBottom: '-1px', fontWeight: tab === t.id ? 600 : 400 }}>
            {t.icon} {t.label}
            <span style={{ fontSize: '11px', background: 'var(--body-bg)', padding: '1px 6px', borderRadius: '10px' }}>
              {keys.filter(k => {
                if (t.id === 'cloud')  {return k.provider_type === 'cloud_api'}
                if (t.id === 'local')  {return k.provider_type === 'local_llm'}
                if (t.id === 'webllm') {return k.provider_type === 'webllm'}
                if (t.id === 'petals') {return k.provider_type === 'petals'}
                return false
              }).length}
            </span>
          </button>
        ))}
        <button onClick={() => { void fetchAll() }}
          style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '8px' }}>
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Key listesi */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '13px', padding: '20px' }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', background: 'var(--card-bg)', border: '1px dashed var(--border-color)', borderRadius: '10px' }}>
          <Key size={32} color="#3b82f6" style={{ margin: '0 auto 12px' }} />
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
            No {tab === 'cloud' ? 'cloud API keys' : tab === 'local' ? 'local LLM endpoints' : tab === 'webllm' ? 'WebLLM registrations' : 'Petals nodes'} yet.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filtered.map(k => (
            <KeyRow key={k.id} k={k}
              showKey={showKey[k.id] ?? false}
              onToggleShow={() => setShowKey(prev => ({ ...prev, [k.id]: !prev[k.id] }))}
              onTest={() => { void handleTest(k.id) }}
              onDelete={() => { void handleDelete(k.id) }}
              onTogglePool={() => { void handleTogglePool(k.id, k.pool_mode) }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Key Satırı ───────────────────────────────────────────────────────────────

interface KeyRowProps {
  k: ApiKey
  showKey: boolean
  onToggleShow: () => void
  onTest: () => void
  onDelete: () => void
  onTogglePool: () => void
}

function KeyRow({ k, showKey, onToggleShow, onTest, onDelete, onTogglePool }: KeyRowProps) {
  const [expanded, setExpanded] = useState(false)

  const statusColor = k.test_result
    ? (k.test_result.online ? '#10b981' : '#ef4444')
    : (k.is_active ? '#10b981' : '#6b7280')

  const statusLabel = k.test_result
    ? (k.test_result.online ? 'Online' : 'Offline')
    : (k.is_active ? 'Active' : 'Inactive')

  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
      {/* Ana satır */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px' }}>
        {/* Status dot */}
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColor, flexShrink: 0 }} />

        {/* Provider icon */}
        {k.provider_type === 'local_llm' ? <Server size={15} color="#8b5cf6" /> :
         k.provider_type === 'webllm'    ? <Zap size={15} color="#f59e0b" /> :
         k.provider_type === 'petals'    ? <Wifi size={15} color="#06b6d4" /> :
         <Globe size={15} color="#3b82f6" />}

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
            {k.key_name}
            {k.pool_mode && (
              <span style={{ fontSize: '10px', padding: '1px 6px', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', borderRadius: '4px' }}>POOL</span>
            )}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            {k.provider} {k.model_name ? `· ${k.model_name}` : ''}
            {k.local_endpoint ? ` · ${k.local_endpoint}` : ''}
          </div>
        </div>

        {/* Masked key */}
        {k.provider_type === 'cloud_api' && (
          <div style={{ fontSize: '12px', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
            {showKey ? k.key_masked : '••••••••••••'}
          </div>
        )}

        {/* Status badge */}
        <div style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px',
          background: statusColor + '20', color: statusColor, fontWeight: 600 }}>
          {k.testing ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : statusLabel}
          {k.test_result?.latency_ms ? ` ${k.test_result.latency_ms}ms` : ''}
        </div>

        {/* Butonlar */}
        <div style={{ display: 'flex', gap: '4px' }}>
          {k.provider_type === 'cloud_api' && (
            <button onClick={onToggleShow} title="Show/hide key"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px' }}>
              {showKey ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          )}
          <button onClick={onTest} title="Test connection" disabled={k.testing}
            style={{ background: 'transparent', border: 'none', cursor: k.testing ? 'not-allowed' : 'pointer', color: '#3b82f6', padding: '4px' }}>
            {k.testing ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Wifi size={13} />}
          </button>
          <button onClick={() => setExpanded(!expanded)} title="Details"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px' }}>
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          <button onClick={onDelete} title="Delete"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px' }}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* Genisletilmis detay */}
      {expanded && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border-color)', background: 'var(--body-bg)', fontSize: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '12px' }}>
            <div><span style={{ color: 'var(--text-secondary)' }}>Usage:</span> {k.usage_count} calls</div>
            <div><span style={{ color: 'var(--text-secondary)' }}>Tokens:</span> {k.total_tokens_used.toLocaleString()}</div>
            <div><span style={{ color: 'var(--text-secondary)' }}>FLZ Earned:</span> <span style={{ color: '#8b5cf6' }}>{k.total_flz_earned.toFixed(4)}</span></div>
          </div>
          {k.test_result && (
            <div style={{ padding: '8px 12px', background: k.test_result.online ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
              border: `1px solid ${k.test_result.online ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
              borderRadius: '6px', color: k.test_result.online ? '#10b981' : '#ef4444', marginBottom: '10px' }}>
              {k.test_result.online ? <Check size={12} style={{ display: 'inline', marginRight: '5px' }} /> : <WifiOff size={12} style={{ display: 'inline', marginRight: '5px' }} />}
              {k.test_result.message}
            </div>
          )}
          {/* Pool toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: 'var(--text-secondary)' }}>Contribute to pool (earn FLZ):</span>
            <button onClick={onTogglePool}
              style={{ padding: '4px 12px', background: k.pool_mode ? '#f59e0b' : 'var(--card-bg)',
                color: k.pool_mode ? 'white' : 'var(--text-secondary)',
                border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
              {k.pool_mode ? 'In Pool ✓' : 'Join Pool'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
